import type { TestPayment } from "../../api/paymentGateway";

/**
 * Opens the gateway's own test checkout for a Rs 1 test order (owner
 * 2026-10-26) and resolves with what confirm needs, or null when the payer
 * closed it. Razorpay: checkout.js, the handler gets payment id and
 * signature. Cashfree: cashfree.js v3 in sandbox, in a modal; Cashfree's
 * word comes from the server, so no id or signature.
 */
export interface CheckoutResult {
  paymentId: string;
  signature: string;
}

const RAZORPAY_JS = "https://checkout.razorpay.com/v1/checkout.js";
const CASHFREE_JS = "https://sdk.cashfree.com/js/v3/cashfree.js";

const loaded = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  let p = loaded.get(src);
  if (!p) {
    p = new Promise<void>((resolve, reject) => {
      const el = document.createElement("script");
      el.src = src;
      el.async = true;
      el.onload = () => resolve();
      el.onerror = () => {
        loaded.delete(src);
        reject(new Error("Could not load the gateway's checkout. Check the connection and try again."));
      };
      document.head.appendChild(el);
    });
    loaded.set(src, p);
  }
  return p;
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_signature: string;
}
interface RazorpayInstance {
  open: () => void;
  on: (event: string, cb: () => void) => void;
}
type RazorpayCtor = new (opts: Record<string, unknown>) => RazorpayInstance;
type CashfreeFactory = (opts: { mode: "sandbox" | "production" }) => {
  checkout: (opts: { paymentSessionId: string; redirectTarget: "_modal" }) => Promise<{ error?: { message?: string } }>;
};

/** Razorpay's checkout for a live or test order; null when the payer closed it. */
export async function openRazorpayCheckout(opts: {
  key: string;
  orderId: string;
  name: string;
  description: string;
  prefill: { name: string; email: string; contact: string };
}): Promise<CheckoutResult | null> {
  await loadScript(RAZORPAY_JS);
  const Razorpay = (window as unknown as { Razorpay?: RazorpayCtor }).Razorpay;
  if (!Razorpay) throw new Error("Razorpay's checkout did not load.");
  return new Promise<CheckoutResult | null>((resolve) => {
    const rp = new Razorpay({
      key: opts.key,
      order_id: opts.orderId,
      name: opts.name,
      description: opts.description,
      prefill: opts.prefill,
      handler: (r: RazorpayResponse) => resolve({ paymentId: r.razorpay_payment_id, signature: r.razorpay_signature }),
      modal: { ondismiss: () => resolve(null) },
    });
    rp.on("payment.failed", () => undefined);
    rp.open();
  });
}

export async function openTestCheckout(tp: TestPayment, payee: string): Promise<CheckoutResult | null> {
  if (tp.connectorname === "cashfree") {
    if (!tp.checkoutsession) throw new Error("Cashfree gave no checkout session for the test order.");
    await loadScript(CASHFREE_JS);
    const cf = (window as unknown as { Cashfree?: CashfreeFactory }).Cashfree;
    if (!cf) throw new Error("Cashfree's checkout did not load.");
    const result = await cf({ mode: "sandbox" }).checkout({ paymentSessionId: tp.checkoutsession, redirectTarget: "_modal" });
    if (result?.error) return null;
    return { paymentId: "", signature: "" };
  }
  await loadScript(RAZORPAY_JS);
  const Razorpay = (window as unknown as { Razorpay?: RazorpayCtor }).Razorpay;
  if (!Razorpay) throw new Error("Razorpay's checkout did not load.");
  return new Promise<CheckoutResult | null>((resolve) => {
    const rp = new Razorpay({
      key: tp.keyid,
      order_id: tp.connectorreference,
      amount: Math.round(tp.amount * 100),
      currency: tp.currency,
      name: payee,
      description: "Test payment (no real money)",
      handler: (r: RazorpayResponse) => resolve({ paymentId: r.razorpay_payment_id, signature: r.razorpay_signature }),
      modal: { ondismiss: () => resolve(null) },
    });
    rp.on("payment.failed", () => undefined);
    rp.open();
  });
}
